import React, { FC } from 'react';

import { Empty, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  TCrmSummary,
  TCrmSummaryRow,
  TCrmSummaryTotals,
} from '../../../../shared/common/api/endpoints/crmAnalyticsApi';
import styles from '../CrmAnalytics.module.scss';
import { bucketLabel, formatMoney, formatNumber, formatPercent } from '../utils';
import { HintTitle } from './common';

interface Props {
  summary: TCrmSummary | null;
  onlyReal: boolean;
}

// Значения строки и итога считаются одними функциями — итог не разъезжается со строками
type TValues = Pick<TCrmSummaryRow,
  'new_schools' | 'cohort_paid' | 'cohort_conversion' | 'new_paying' | 'returned' | 'churned'
  | 'churned_grace' | 'churned_stopped' | 'churn_rate' | 'paying_start' | 'cash_real' | 'cash_bonus'
  | 'cash_excluded' | 'accrual_real' | 'accrual_bonus' | 'partner_commission'>;

const cashOf = (values: TValues, onlyReal: boolean): number =>
  values.cash_real + (onlyReal ? 0 : values.cash_bonus + values.cash_excluded);

const accrualOf = (values: TValues, onlyReal: boolean): number =>
  values.accrual_real + (onlyReal ? 0 : values.accrual_bonus);

const churnCell = (values: TValues, final: boolean) => (
  <Tooltip
    title={(
      <>
        <div>Перестали платить: {formatNumber(values.churned_stopped)}</div>
        <div>Выручка упала → льготный: {formatNumber(values.churned_grace)}</div>
        {!final && <div>Последние 30 дней ещё досчитываются</div>}
      </>
    )}
  >
    <span>{formatNumber(values.churned)}{final ? '' : '*'}</span>
  </Tooltip>
);

const SummaryTable: FC<Props> = ({ summary, onlyReal }) => {
  if (!summary) return <Empty description="Нет данных" />;

  const { granularity, totals } = summary;
  const finalTotals = summary.rows.every((row) => row.churn_final);

  const columns: ColumnsType<TCrmSummaryRow> = [
    {
      title: 'Период',
      dataIndex: 'bucket',
      fixed: 'left',
      width: 120,
      render: (bucket: string) => bucketLabel(bucket, granularity),
    },
    {
      title: <HintTitle title="Подключились" hint="Новых школ подключено в отрезке" />,
      dataIndex: 'new_schools',
      align: 'right',
      render: formatNumber,
    },
    {
      title: <HintTitle title="Стали покупателями" hint="Сколько из подключившихся в отрезке уже сделали первый реальный платёж (когда угодно) и их доля" />,
      key: 'cohort',
      align: 'right',
      render: (_, row) => (row.new_schools > 0
        ? `${formatNumber(row.cohort_paid)} · ${formatPercent(row.cohort_conversion)}`
        : '—'),
    },
    {
      title: <HintTitle title="Начали платить" hint="Первый реальный платёж школы пришёлся на отрезок" />,
      dataIndex: 'new_paying',
      align: 'right',
      render: formatNumber,
    },
    {
      title: <HintTitle title="Вернулись" hint="Снова получили оплаченный доступ после перерыва больше 30 дней" />,
      dataIndex: 'returned',
      align: 'right',
      render: formatNumber,
    },
    {
      title: <HintTitle title="Ушли" hint="30 дней подряд без оплаченного доступа; датируется первым днём без доступа. * — отрезок моложе 30 дней, число вырастет" />,
      key: 'churned',
      align: 'right',
      render: (_, row) => churnCell(row, row.churn_final),
    },
    {
      title: <HintTitle title="Отток" hint="Ушедшие / (платили на начало + начали платить + вернулись)" />,
      dataIndex: 'churn_rate',
      align: 'right',
      render: formatPercent,
    },
    {
      title: <HintTitle title="Платили на начало" hint="Школ с оплаченным доступом в день перед началом отрезка" />,
      dataIndex: 'paying_start',
      align: 'right',
      render: formatNumber,
    },
    {
      title: <HintTitle title="Платят на конец" hint="Школ с оплаченным доступом в последний день отрезка" />,
      key: 'paying_end',
      align: 'right',
      render: (_, row) => formatNumber(row.statuses?.paying),
    },
    {
      title: <HintTitle title="Поступления" hint={onlyReal ? 'Реальные платежи по дате платежа' : 'Все платежи: реальные, бонусные, «Не учитывать» и возвраты'} />,
      key: 'cash',
      align: 'right',
      render: (_, row) => formatMoney(cashOf(row, onlyReal)),
    },
    {
      title: <HintTitle title="Начислено" hint={onlyReal ? 'Списания за доступ с основного счёта' : 'Все списания за доступ, с основного и бонусного счёта'} />,
      key: 'accrual',
      align: 'right',
      render: (_, row) => formatMoney(accrualOf(row, onlyReal)),
    },
    {
      title: <HintTitle title="Комиссия партнёров" hint="Начислено партнёрам по платежам отрезка" />,
      dataIndex: 'partner_commission',
      align: 'right',
      render: formatMoney,
    },
    {
      title: <HintTitle title="Начислено на школу" hint="Начислено с основного счёта / школ с оплаченным доступом хотя бы день в отрезке" />,
      dataIndex: 'arppu',
      align: 'right',
      render: formatMoney,
    },
    {
      title: <HintTitle title="На балансах" hint="Положительные остатки основных счетов на конец отрезка — оплачено, ещё не отработано" />,
      dataIndex: 'balance_main',
      align: 'right',
      render: formatMoney,
    },
  ];

  const totalsRow = (values: TCrmSummaryTotals) => (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}><b>Итого</b></Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">{formatNumber(values.new_schools)}</Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          {`${formatNumber(values.cohort_paid)} · ${formatPercent(values.cohort_conversion)}`}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">{formatNumber(values.new_paying)}</Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">{formatNumber(values.returned)}</Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">{churnCell(values, finalTotals)}</Table.Summary.Cell>
        <Table.Summary.Cell index={6} align="right">{formatPercent(values.churn_rate)}</Table.Summary.Cell>
        <Table.Summary.Cell index={7} align="right">{formatNumber(values.paying_start)}</Table.Summary.Cell>
        <Table.Summary.Cell index={8} align="right">{formatNumber(values.paying_end)}</Table.Summary.Cell>
        <Table.Summary.Cell index={9} align="right">{formatMoney(cashOf(values, onlyReal))}</Table.Summary.Cell>
        <Table.Summary.Cell index={10} align="right">{formatMoney(accrualOf(values, onlyReal))}</Table.Summary.Cell>
        <Table.Summary.Cell index={11} align="right">{formatMoney(values.partner_commission)}</Table.Summary.Cell>
        <Table.Summary.Cell index={12} align="right">—</Table.Summary.Cell>
        <Table.Summary.Cell index={13} align="right">{formatMoney(values.balance_main)}</Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );

  return (
    <Table<TCrmSummaryRow>
      className={styles.table}
      rowKey="bucket"
      size="small"
      columns={columns}
      dataSource={summary.rows}
      pagination={false}
      scroll={{ x: 'max-content' }}
      summary={() => totalsRow(totals)}
    />
  );
};

export default SummaryTable;
